// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface Props {
    isShip?: boolean;
    warehouse?: string;
    isInStock?: boolean;
    isPartial?: boolean;
    isOnBackOrder?: boolean;
    editable?: boolean;
    isCustom?: boolean;
    showQtyOrderedShipped?: boolean;
    injectedColumnsLabels?: { product?: string; total?: string };
    isSeacoastCustom?: boolean;
}

interface GroupedLinesHeading {
    wrapper?: InjectableCss;
    actionHeadingWrapper?: InjectableCss;
}

const groupedLinesStyles: GroupedLinesHeading = {
    wrapper: {
        css: css`
            display: flex;
            padding: 10px;
            background: ${getColor("common.ui2")};
            align-items: center;
            box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.3);

            &.in-stock {
                border-top: 4px solid;
                border-top-color: ${getColor("success.main")};
            }

            &.partial {
                border-top: 4px solid;
                border-top-color: ${getColor("partial.main")};
            }

            &.back-order {
                border-top: 4px solid;
                border-top-color: ${getColor("warning.main")};
            }

            &.seacoast {
                border-top: 4px solid;
                border-top-color: ${getColor("primary.main")};
                background-color: #eee;
                box-shadow: none;
            }

            @media print {
                display: none;
            }
        `,
    },
    actionHeadingWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-grow: 1;
            align-items: center;
        `,
    },
};

const GroupedLinesHeader: FC<Props> = ({
    isShip,
    warehouse,
    isInStock,
    isPartial,
    isOnBackOrder,
    editable,
    isCustom,
    showQtyOrderedShipped,
    injectedColumnsLabels,
    isSeacoastCustom,
}) => {
    const className = isInStock ? "in-stock" : isPartial ? "partial" : isOnBackOrder ? "back-order" : undefined;

    let descriptionWidth = css`
        width: 60%;
    `;
    let notEditableDescriptionWidth = css`
        width: 62%;
    `;
    let columnWidth = css`
        width: calc(100% / 3);
    `;

    if (showQtyOrderedShipped) {
        descriptionWidth = css`
            width: 55%;
        `;
        notEditableDescriptionWidth = css`
            width: 57%;
        `;
        columnWidth = css`
            width: calc(100% / 4);
        `;
    }

    const descriptionStyles = css`
        ${descriptionWidth};

        &.not-editable {
            ${notEditableDescriptionWidth}
        }

        @media (max-width: 800px) {
            width: 100% !important;
        }
    `;

    const columnStyles = css`
        ${columnWidth};
        text-align: center;

        &.not-editable.qty {
            padding-left: 0;
            padding-right: 11px;
        }
        &.qty-only {
            text-align: left;
            padding-left: 15px;
            width: 80px;
        }

        &.total-column {
            text-align: right;
        }

        @media (max-width: 800px) {
            display: none;
        }

        &.addPadding {
            text-align: left;
        }
    `;
    
    if (isSeacoastCustom) {
        return (
            <StyledWrapper {...groupedLinesStyles.wrapper} className="seacoast">
                <StyledWrapper css={descriptionStyles} className={editable ? "" : "not-editable"}>
                    <Typography size="16px" lineHeight="22px">
                        {translate("Item Description")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...groupedLinesStyles.actionHeadingWrapper}>
                    <StyledWrapper
                        css={columnStyles}
                        className={`qty-only ${editable ? "" : "not-editable qty"}`}
                    >
                        <Typography size="16px" lineHeight="22px">
                            {translate("Qty")}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        )
    }

    return (
        <StyledWrapper {...groupedLinesStyles.wrapper} className={className}>
            <StyledWrapper css={descriptionStyles} className={editable ? "" : "not-editable"}>
                {isInStock && isShip && (
                    <Typography size="16px" lineHeight="22px">
                        {translate("Available For Shipping")}
                    </Typography>
                )}
                {isInStock && !isShip && (
                    <Typography size="16px" lineHeight="22px">
                        {translate("Available For Pick Up From ")}
                        <Typography size="16px" lineHeight="22px" as="strong">
                            {warehouse}
                        </Typography>
                    </Typography>
                )}
                {isPartial && isShip && (
                    <Typography size="16px" lineHeight="22px">
                        {translate("Partially Available For Shipping")}
                    </Typography>
                )}
                {isPartial && !isShip && (
                    <Typography>
                        {translate("Partially Available For Pick Up From ")}
                        <Typography size="16px" lineHeight="22px" as="strong">
                            {warehouse}
                        </Typography>
                    </Typography>
                )}
                {isOnBackOrder && (
                    <Typography size="16px" lineHeight="22px">
                        {translate("Products On Backorder")}
                    </Typography>
                )}
                {isCustom && (
                    <Typography>
                        {translate("Products Shipping from ")}
                        <Typography size="16px" lineHeight="22px" as="strong">
                            {warehouse}
                        </Typography>
                    </Typography>
                )}
                {!isInStock && !isPartial && !isOnBackOrder && !isCustom && (
                    <Typography size="16px" lineHeight="22px">
                        {injectedColumnsLabels?.product ? injectedColumnsLabels?.product : translate("Products")}
                    </Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...groupedLinesStyles.actionHeadingWrapper}>
                <StyledWrapper
                    css={columnStyles}
                    className={`${!showQtyOrderedShipped ? "qty-only" : ""} ${editable ? "" : "not-editable qty"}`}
                >
                    <Typography size="16px" lineHeight="22px">
                        {showQtyOrderedShipped ? translate("Qty Ordered") : translate("Qty")}
                    </Typography>
                </StyledWrapper>
                {showQtyOrderedShipped && (
                    <StyledWrapper css={columnStyles} className={editable ? "" : "not-editable"}>
                        <Typography size="16px" lineHeight="22px">
                            {translate("Qty Shipped")}
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper
                    css={columnStyles}
                    className={!isInStock && !isPartial && !isOnBackOrder && !isCustom ? "addPadding" : ""}
                >
                    <Typography size="16px" lineHeight="22px">
                        {translate("Price/Unit")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper css={columnStyles} className={showQtyOrderedShipped ? "total-column" : ""}>
                    <Typography size="16px" lineHeight="22px">
                        {injectedColumnsLabels?.total ? injectedColumnsLabels?.total : translate("Total")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default GroupedLinesHeader;
